import React, { lazy, Suspense } from 'react'
import { Routes as RouterRoutes, Route } from 'react-router-dom';
import { RouteHandler } from './components';
import RouteMeta from './components/RouteMeta';

// Immediate load - ONLY critical pages for initial render
import {
  Home,
  NotFound,
  SignIn,
  SignUp,
  Ads,
} from './pages';

// Lazy load - all other pages (including heavy components)
const About = lazy(() => import('./pages/About'));
const AdPage = lazy(() => import('./pages/AdPage'));
const AddAd = lazy(() => import('./pages/AddAd'));
const EditAd = lazy(() => import('./pages/EditAd'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ResetPasswordCode = lazy(() => import('./pages/ResetPasswordCode'));
const TwoFactorSettings = lazy(() => import('./pages/TwoFactorSettings'));
const TwoFactorVerify = lazy(() => import('./pages/TwoFactorVerify'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Messages = lazy(() => import('./pages/Messages'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SavedSearches = lazy(() => import('./pages/SavedSearches'));
const Statistics = lazy(() => import('./pages/Statistics'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));

// Offer pages
const ReceivedOffers = lazy(() => import('./pages/MyOffers/Received'));
const SentOffers = lazy(() => import('./pages/MyOffers/Sent'));

// Admin pages - lazy load
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/Admin/Users'));
const AdminAds = lazy(() => import('./pages/Admin/Ads'));
const AdminEditAd = lazy(() => import('./pages/Admin/Ads/EditAd'));
const AdminReports = lazy(() => import('./pages/Admin/Reports'));
const AdminStatistics = lazy(() => import('./pages/Admin/Statistics'));
const AdminSecurity = lazy(() => import('./pages/Admin/Security'));

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    fontSize: '18px',
    color: '#667eea'
  }}>
    <i className="fa fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
    Cargando...
  </div>
);

const Routes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      {/* title/description/canonical/OG por ruta (todas las que no son /ad/:id) */}
      <RouteMeta />
      <RouterRoutes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/ad/:id" element={<AdPage />} />
        <Route path="/ads" element={<Ads />} />
        <Route path="/post-an-add" element={<RouteHandler isPrivate={true}><AddAd /></RouteHandler>} />
        <Route path="/edit-ad/:id" element={<RouteHandler isPrivate={true}><EditAd /></RouteHandler>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password-code" element={<ResetPasswordCode />} />
        <Route path="/2fa-settings" element={<RouteHandler isPrivate={true}><TwoFactorSettings /></RouteHandler>} />
        <Route path="/verify-2fa" element={<TwoFactorVerify />} />
        <Route path="/auth/google/success" element={<GoogleCallback />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/my-account" element={<MyAccount />} />
        <Route path="/favorites" element={<RouteHandler isPrivate={true}><Favorites /></RouteHandler>} />
        <Route path="/messages" element={<RouteHandler isPrivate={true}><Messages /></RouteHandler>} />
        <Route path="/messages/:adPublicId" element={<RouteHandler isPrivate={true}><Messages /></RouteHandler>} />
        <Route path="/dashboard" element={<RouteHandler isPrivate={true}><Dashboard /></RouteHandler>} />
        <Route path="/saved-searches" element={<RouteHandler isPrivate={true}><SavedSearches /></RouteHandler>} />
        <Route path="/statistics" element={<RouteHandler isPrivate={true}><Statistics /></RouteHandler>} />
        <Route path="/profile-settings" element={<RouteHandler isPrivate={true}><ProfileSettings /></RouteHandler>} />
        <Route path="/user/:userId" element={<UserProfilePage />} />
        <Route path="/offers/received" element={<RouteHandler isPrivate={true}><ReceivedOffers /></RouteHandler>} />
        <Route path="/offers/sent" element={<RouteHandler isPrivate={true}><SentOffers /></RouteHandler>} />
        <Route path="/admin/dashboard" element={<RouteHandler isPrivate={true}><AdminDashboard /></RouteHandler>} />
        <Route path="/admin/users" element={<RouteHandler isPrivate={true}><AdminUsers /></RouteHandler>} />
        <Route path="/admin/ads" element={<RouteHandler isPrivate={true}><AdminAds /></RouteHandler>} />
        <Route path="/admin/ads/edit/:id" element={<RouteHandler isPrivate={true}><AdminEditAd /></RouteHandler>} />
        <Route path="/admin/reports" element={<RouteHandler isPrivate={true}><AdminReports /></RouteHandler>} />
        <Route path="/admin/statistics" element={<RouteHandler isPrivate={true}><AdminStatistics /></RouteHandler>} />
        <Route path="/admin/security" element={<RouteHandler isPrivate={true}><AdminSecurity /></RouteHandler>} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;